import {
  CHANGE_KIND_PROPOSE,
  type ChangeEvidence,
  type ChangeKind,
  OWNER_CONFIG_KINDS,
  OWNER_KINDS,
  type Row,
} from '@contract';
import { parseAmount } from '@sao/shared/money';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { FieldError } from '@/components/field-error';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { callAdmin } from '@/lib/api';
import { confirmSecondFactor } from '@/lib/auth';
import { homeCurrency } from '@/lib/market';
import { invalidate, queryClient } from '@/lib/query';
import { useStaff } from '@/lib/staff-context';
import { toast } from '@/lib/toast';

/** One value the maker fills in, merged into the payload. */
export interface ProposeField {
  name: string;
  label: string;
  /** `amount` is typed in cedis and sent as pesewas. */
  kind: 'select' | 'text' | 'amount';
  options?: readonly { value: string; label: string }[];
  currency?: string;
  hint?: string;
  /** A field that may stay empty; an empty one is left out of the payload. */
  optional?: boolean;
  /** Send an amount as a JSON number, not a string of digits. */
  asNumber?: boolean;
  /**
   * An amount that may be cleared: typing "none" sends null ("no limit"). A
   * blank optional field is still left out, which leaves the value as it is.
   */
  clearable?: boolean;
}

/** The words that clear a clearable amount. */
const CLEAR_WORDS = ['none', 'no limit'];

export function isClearWord(field: ProposeField, input: string): boolean {
  return (
    field.kind === 'amount' &&
    field.clearable === true &&
    CLEAR_WORDS.includes(input.trim().toLowerCase())
  );
}

/** One thing the request changes, before and after, in words. */
interface ChangeLine {
  label: string;
  before: string;
  after: string;
}

interface ProposeProps {
  kind: ChangeKind;
  /** What the request acts on, fixed by the page. */
  payload: Row;
  label: string;
  title: string;
  description: string;
  fields?: readonly ProposeField[];
  /** Put the fields under this key of the payload (`values`), not beside the fixed part. */
  into?: string;
  /** Links the request carries: the transaction, report or line it is about. */
  evidence?: readonly ChangeEvidence[];
  /** Shown above the reason so the maker sees what changes (display only; not sent). */
  changes?: readonly ChangeLine[];
  variant?: 'outline' | 'default' | 'secondary' | 'destructive';
  /** Called once the request is sent (or applied, for the owner). */
  onDone?: () => void;
}

/** Whether this kind is one the super admin does at once, with no second approver. */
function useActsAsOwner(kind: ChangeKind): boolean {
  const staff = useStaff();
  return (
    staff.is_super_admin && (OWNER_KINDS as readonly string[]).includes(kind)
  );
}

/** Configuration the super admin changes alone needs a fresh authenticator code. */
function needsCode(kind: ChangeKind): boolean {
  return (OWNER_CONFIG_KINDS as readonly string[]).includes(kind);
}

/** Whether the signed-in staff member may propose this kind (or, as the super admin, do it). */
export function useCanPropose(kind: ChangeKind): boolean {
  const staff = useStaff();
  const owner = useActsAsOwner(kind);
  return (
    owner ||
    CHANGE_KIND_PROPOSE[kind].some((permission) =>
      staff.permissions.includes(permission),
    )
  );
}

/**
 * A button that proposes a change request. Hidden from staff who cannot
 * propose this kind; the server decides either way.
 */
export function ProposeChange(props: Readonly<ProposeProps>) {
  const [open, setOpen] = useState(false);
  if (!useCanPropose(props.kind)) return null;
  return (
    <>
      <Button
        size="sm"
        variant={props.variant ?? 'outline'}
        onClick={() => setOpen(true)}
      >
        {props.label}
      </Button>
      {open ? (
        <ProposeDialog {...props} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

function ProposeDialog({
  kind,
  payload,
  title,
  description,
  fields = [],
  into,
  evidence = [],
  changes = [],
  onDone,
  onClose,
}: Readonly<ProposeProps & { onClose: () => void }>) {
  const navigate = useNavigate();
  const owner = useActsAsOwner(kind);
  const askCode = owner && needsCode(kind);
  const propose = useMutation({
    mutationFn: async ({
      code,
      ...args
    }: {
      payload: Row;
      reason: string;
      evidence: ChangeEvidence[];
      code: string;
    }) => {
      if (owner) {
        if (askCode) await confirmSecondFactor(code);
        const done = await callAdmin('customers.act', {
          kind: kind as (typeof OWNER_KINDS)[number],
          payload: args.payload,
          reason: args.reason,
        });
        return { owner: true as const, summary: done.summary };
      }
      const request = await callAdmin('changes.propose', { kind, ...args });
      return { owner: false as const, request };
    },
    onSuccess: async (outcome) => {
      onClose();
      onDone?.();
      if (outcome.owner) {
        // A configuration change can show on any page.
        await (askCode
          ? queryClient.invalidateQueries({ queryKey: ['admin'] })
          : invalidate('customers.tab', 'customers.get', 'customers.list'));
        toast.success('Done', { description: outcome.summary });
        return;
      }
      await invalidate('changes.list');
      toast.success('Sent for approval', {
        description: outcome.request.summary,
        action: {
          label: 'Open',
          onClick: () =>
            void navigate({
              to: '/approvals/$requestId',
              params: { requestId: outcome.request.id },
            }),
        },
      });
    },
  });

  const form = useForm({
    defaultValues: {
      reason: '',
      note: '',
      code: '',
      values: Object.fromEntries(
        fields.map((field) => [field.name, field.options?.[0]?.value ?? '']),
      ) as Record<string, string>,
    },
    validators: { onSubmit: proposeSchema(fields, askCode) },
    onSubmit: ({ value }) => {
      const extra = Object.fromEntries(
        fields
          .filter(
            (field) =>
              !field.optional || (value.values[field.name] ?? '').trim(),
          )
          .map((field) => [
            field.name,
            payloadValue(field, value.values[field.name] ?? ''),
          ]),
      );
      const note = value.note.trim();
      return propose
        .mutateAsync({
          payload: into
            ? { ...payload, [into]: extra }
            : { ...payload, ...extra },
          reason: value.reason.trim(),
          code: value.code.trim(),
          evidence: [
            ...evidence,
            ...(note ? [{ type: 'note' as const, ref: note }] : []),
          ],
        })
        .catch(() => undefined);
    },
  });

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description} {ownerNote(owner, askCode)}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          {changes.length > 0 ? (
            <ChangeLines changes={changes} owner={owner} />
          ) : null}
          {fields.map((spec) => (
            <form.Field key={spec.name} name={`values.${spec.name}`}>
              {(field) => (
                <div className="flex flex-col gap-2">
                  {spec.kind === 'select' ? null : (
                    <Label htmlFor={`propose-${spec.name}`}>{spec.label}</Label>
                  )}
                  {spec.kind === 'select' ? (
                    <Select
                      label={spec.label}
                      value={field.state.value ?? ''}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    >
                      {spec.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      id={`propose-${spec.name}`}
                      inputMode={spec.kind === 'amount' ? 'decimal' : undefined}
                      value={field.state.value ?? ''}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                  )}
                  {spec.hint ? (
                    <p className="text-xs text-muted-foreground">{spec.hint}</p>
                  ) : null}
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
          ))}
          <form.Field name="reason">
            {(field) => (
              <div className="flex flex-col gap-2">
                <Label htmlFor="propose-reason">Reason</Label>
                <Textarea
                  id="propose-reason"
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {owner
                    ? '8 to 500 characters, kept in the audit log.'
                    : '8 to 500 characters. The checker reads it, and it is kept in the audit log.'}
                </p>
                <FieldError errors={field.state.meta.errors} />
              </div>
            )}
          </form.Field>
          {evidence.length > 0 ? (
            <div className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Evidence attached</span>
              {evidence.map((item) => (
                <span
                  key={`${item.type}:${item.ref}`}
                  className="text-muted-foreground"
                >
                  {EVIDENCE_LABEL[item.type]}{' '}
                  <span className="font-mono text-xs">{item.ref}</span>
                </span>
              ))}
            </div>
          ) : null}
          {askCode ? (
            <form.Field name="code">
              {(field) => (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="propose-code">Authenticator code</Label>
                  <Input
                    id="propose-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="6-digit code"
                    className="w-full sm:w-44"
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
          ) : null}
          <form.Field name="note">
            {(field) => (
              <div className={owner ? 'hidden' : 'flex flex-col gap-2'}>
                <Label htmlFor="propose-note">Note for the checker</Label>
                <Input
                  id="propose-note"
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                />
                <FieldError errors={field.state.meta.errors} />
              </div>
            )}
          </form.Field>
          {propose.error ? (
            <Alert variant="destructive">{propose.error.message}</Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" disabled={isSubmitting}>
                  {owner ? 'Apply now' : 'Send for approval'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChangeLines({
  changes,
  owner,
}: Readonly<{ changes: readonly ChangeLine[]; owner: boolean }>) {
  return (
    <dl className="flex flex-col gap-3 rounded-lg border bg-surface-2/40 p-3 text-sm">
      {changes.map((line) => (
        <div key={line.label} className="flex flex-col gap-1">
          <dt className="font-medium">{line.label}</dt>
          <dd className="whitespace-pre-line text-muted-foreground">
            <span className="text-xs uppercase tracking-wide">Now</span>
            {'\n'}
            {line.before}
          </dd>
          <dd className="whitespace-pre-line">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              {owner ? 'After' : 'After approval'}
            </span>
            {'\n'}
            {line.after}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Posted or failed: how a stuck payment or provider leg is resolved. */
export const OUTCOME_FIELD: ProposeField = {
  name: 'outcome',
  label: 'Outcome',
  kind: 'select',
  options: [
    { value: 'posted', label: 'It went through: post it' },
    { value: 'failed', label: 'It did not: fail it and release the hold' },
  ],
  hint: 'Check the provider or the core first; the answer is final.',
};

const EVIDENCE_LABEL: Record<ChangeEvidence['type'], string> = {
  transaction: 'Transaction',
  issue: 'Issue report',
  statement_line: 'Statement line',
  break: 'Break',
  case: 'Case',
  file: 'File',
  note: 'Note',
};

/** What the dialog says about who approves it. */
function ownerNote(owner: boolean, askCode: boolean): string {
  if (!owner) return 'Someone else approves it before anything changes.';
  if (askCode) {
    return 'You own this: it applies at once with your authenticator code, without a second person. It is logged with your reason, and the ops alert list is told.';
  }
  return 'You own this: it applies at once and is logged with your reason.';
}

function proposeSchema(fields: readonly ProposeField[], askCode: boolean) {
  const values = Object.fromEntries(
    fields.map((field) => [field.name, fieldSchema(field)]),
  );
  return z.object({
    code: askCode
      ? z
          .string()
          .trim()
          .regex(/^\d{6}$/, 'Enter the 6-digit code.')
      : z.string(),
    reason: z
      .string()
      .trim()
      .min(8, 'Give a reason of at least 8 characters.')
      .max(500, 'Keep the reason under 500 characters.'),
    note: z.string().trim().max(200, 'Keep the note under 200 characters.'),
    values: z.object(values),
  });
}

/** What a field sends: an amount in minor units, an optional field trimmed. */
export function payloadValue(
  field: ProposeField,
  input: string,
): string | number | null {
  if (isClearWord(field, input)) return null;
  if (field.kind === 'amount') {
    const minor = parseAmount(input, field.currency ?? homeCurrency());
    return field.asNumber ? Number(minor) : String(minor);
  }
  return field.optional ? input.trim() : input;
}

/** How a field is checked before the proposal goes. */
export function fieldSchema(field: ProposeField) {
  if (field.kind === 'amount') {
    return z
      .string()
      .refine(
        (input) =>
          (field.optional && input.trim() === '') ||
          isClearWord(field, input) ||
          (parseAmount(input, field.currency ?? homeCurrency()) ?? 0) > 0,
        field.clearable
          ? 'Enter an amount above zero, or none for no limit.'
          : 'Enter an amount above zero.',
      );
  }
  if (field.optional) return z.string();
  return z.string().trim().min(1, `Fill in ${field.label.toLowerCase()}.`);
}
